import { Trans } from "@lingui/react/macro";
import type { ITitleInputFields } from "@/forms/AddActivityForm/TitleInput";
import { TitleInput } from "@/forms/AddActivityForm/TitleInput";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { FormProvider, useForm } from "react-hook-form";
import styles from "./QuickActivityInDetails.module.scss";
import { LoadingOverlay } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import { getPatientNameWithStatus } from "@/api/Patients";
import type { ICompetencePickerFields } from "@/forms/AddActivityForm/CompetencePicker";
import { CompetencePicker } from "@/forms/AddActivityForm/CompetencePicker";
import { Heading } from "@medoma/components/Heading/Heading";
import { generateRandomUUID } from "@/Utils/UniqueId";
import Form from "@/components/Form/Form";
import type { INewActivityInGroup } from "@/api/Activities";
import { addActivityInGroup } from "@/api/Activities";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  getGroupOrVisitStatus,
  timeOfDayDictionary,
  visitStatusSchema,
  type IGroupOfActivityOccurrence,
} from "@medoma/models/activities";
import { dateName } from "@/Utils/DateUtils";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { AssignedShiftChip } from "@/components/Chips/AssignedShiftChip";
import { flushSync } from "react-dom";
import { useLingui } from "@lingui/react/macro";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import {
  DescriptionInput,
  type IDescriptionInputFields,
} from "@/forms/AddActivityForm/DescriptionInput";
import {
  ActivityDuration,
  type IActivityDurationFields,
} from "@/forms/AddActivityForm/ActivityDuration";
import { DEFAULT_ACTIVITY_DURATION_OPTION } from "@/forms/AddActivityForm/activityDurationModels";
import { convertDurationToMinutes } from "@/forms/AddActivityForm/activityDurationUtils";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import Chip from "@medoma/components/Chip/Chip";

const GROUP_DATE_SUFFIX_LENGTH = 8;
export const QuickActivityInDetails = ({
  group,
}: {
  group: IGroupOfActivityOccurrence;
}) => {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);

  const { data: showDuration } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const methods = useForm<
    ITitleInputFields &
      IDescriptionInputFields &
      ICompetencePickerFields &
      IActivityDurationFields
  >({
    defaultValues: {
      durationOption: DEFAULT_ACTIVITY_DURATION_OPTION,
    },
  });
  const {
    setError,
    reset,
    getValues,
    formState: { errors, isDirty },
  } = methods;

  const queryClient = useQueryClient();
  const { mutate: addNewActivity, isPending: isAdding } = useMutation({
    mutationFn: ({ newActivity }: { newActivity: INewActivityInGroup }) =>
      addActivityInGroup(newActivity),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries();
      // Use `flushSync` to avoid flicker where `onSuccess` is done, while `setOpen` is not quite done.
      flushSync(() => {
        setOpen(false);
      });
      reset();
    },
  });

  const { confirm } = useDialog();

  const groupId = group.id;
  const visitStatus = getGroupOrVisitStatus(group);

  const activityOccurrenceIsAddable = async () => {
    if (
      visitStatus !== visitStatusSchema.enum.ongoing &&
      visitStatus !== visitStatusSchema.enum.travellingTo
    ) {
      return true;
    }
    return await confirm({
      title: t`Lägg till aktivitet`,
      message: t`Är du säker på att du vill lägga till en aktivitet i ett pågående besök?`,
    });
  };

  const validateAndSubmit = methods.handleSubmit(async (validatedFormData) => {
    const {
      requiredCompetences,
      title,
      description,
      durationOption,
      customDuration,
      customDurationUnit,
    } = validatedFormData;

    // Convert duration fields to minutes
    const duration = convertDurationToMinutes(
      durationOption,
      customDuration,
      customDurationUnit,
    );

    const newActivity: INewActivityInGroup = {
      activityId: generateRandomUUID(),
      groupId,
      requiredCompetences,
      title,
      description,
      duration,
    };

    if (await activityOccurrenceIsAddable()) {
      addNewActivity({ newActivity });
    }
  });

  const dateString = groupId.substring(
    groupId.length - GROUP_DATE_SUFFIX_LENGTH,
  );
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);
  const date = isNaN(new Date(`${year}-${month}-${day}T00:00:00`).getTime())
    ? null
    : new Date(`${year}-${month}-${day}T00:00:00`);

  return (
    <>
      {open ? (
        <>
          <PlainButton
            size="small"
            weight="light"
            onClick={() => setOpen(false)}
          >
            <Trans>Avbryt</Trans>
          </PlainButton>
          <div className={styles.container}>
            <FormProvider {...methods}>
              <LoadingOverlay
                show={isAdding}
                message={t`Lägger till aktiviteten`}
              >
                <Form onSubmit={validateAndSubmit}>
                  {errors.root?.server?.message && !isDirty ? (
                    <ErrorMessage message={errors.root.server.message} />
                  ) : undefined}
                  <TitleInput />
                  <DescriptionInput />
                  {showDuration ? <ActivityDuration /> : null}
                  <CompetencePicker isRequired />
                  <div>
                    <Heading level="h2" size="h3" weight="medium">
                      <Trans>Övrigt</Trans>
                    </Heading>
                    <div className={styles.additionalConfig}>
                      <CategoryIcon category={group.category} size="small" />
                      <Chip>
                        {getPatientNameWithStatus(group.patient, "full")}
                      </Chip>
                      {date ? <Chip>{dateName(date)}</Chip> : null}
                      <Chip>{t(timeOfDayDictionary.Any.long)}</Chip>
                      {group.assignees?.map((assignee) => (
                        <AssignedShiftChip
                          key={assignee.id}
                          medicalCompetence={assignee.competence}
                          shift={assignee}
                          size="small"
                        />
                      ))}
                    </div>
                  </div>
                  <FilledButton disabled={isAdding} type="submit">
                    <Trans>Skapa aktiviteten</Trans>
                  </FilledButton>
                </Form>
              </LoadingOverlay>
            </FormProvider>
          </div>
        </>
      ) : (
        <PlainButton size="small" weight="light" onClick={() => setOpen(true)}>
          <Trans>Lägg till aktivitet</Trans>
        </PlainButton>
      )}
    </>
  );
};
